import * as React from 'react';
import { storyblokEditable, type SbBlokData } from '@storyblok/react';

import type { ProfileCardStoryblok, StoryblokMultilink } from '../../types/storyblok';

interface ProfileCardProps {
  blok: ProfileCardStoryblok;
}

/**
 * Resolves the href from a Storyblok multilink field.
 * Prefers cached_url over url.
 */
const resolveLinkHref = (link: StoryblokMultilink | undefined): string | null => {
  if (!link) return null;
  const href = link.cached_url || link.url;
  return href || null;
};

export const ProfileCard: React.FC<ProfileCardProps> = ({ blok }) => {
  const linkHref = resolveLinkHref(blok.link as StoryblokMultilink | undefined);
  const hasLink = Boolean(linkHref);

  // Badge row: all-or-nothing - only render when both badge_icon and badge_label are present
  const showBadge = Boolean(blok.badge_icon?.filename && blok.badge_label);

  // Image alt: use asset alt if present, otherwise empty for decorative images
  const imageAlt = blok.image?.alt ?? '';
  const imageSrc = blok.image?.filename || undefined;

  // Badge icon alt text
  const badgeIconAlt = blok.badge_icon?.alt ?? '';
  const badgeIconSrc = blok.badge_icon?.filename || undefined;

  // Shared classes for the card surface
  const cardClasses = [
    'bg-neutral-white',
    'rounded-lg',
    'shadow-[0px_4px_64px_0px_rgba(28,43,58,0.16)]',
    'p-6',
    'flex',
    'flex-col',
    'gap-6',
  ].join(' ');

  // Classes for link wrapper (hover/focus states)
  // Hover changes background color (no shadow change) per #90; focus mirrors hover for keyboard parity.
  const linkClasses = [
    cardClasses,
    'block',
    'transition-colors',
    'hover:bg-neutral-off-white',
    'focus-visible:bg-neutral-off-white',
    'focus-visible:outline-none',
    'focus-visible:ring-2',
    'focus-visible:ring-brand-blue',
    'focus-visible:ring-offset-2',
  ].join(' ');

  // Editable attributes for Storyblok Visual Editor
  const editableProps = storyblokEditable(blok as unknown as SbBlokData);

  // Card content - shared between <a> and <article>
  const cardContent = (
    <>
      {/* Image container with muted background */}
      <div className="bg-gray-100 rounded-[14px] overflow-hidden flex items-center justify-center w-[100px] h-[100px]">
        {imageSrc && <img src={imageSrc} alt={imageAlt} className="w-[64px] h-[35px]" />}
      </div>

      {/* Text content and badge - grouped with 10px gap per Figma */}
      <div className="flex flex-col gap-2.5">
        <h3 className="text-caption font-bold text-brand-deep">{blok.title}</h3>
        {blok.subtitle && <p className="text-callout text-gray-700">{blok.subtitle}</p>}
        {/* Badge row - only when both icon and label are present */}
        {showBadge && (
          <div className="flex items-center gap-2.5">
            {badgeIconSrc && (
              <div className="bg-white border border-gray-100 rounded-full p-1 flex items-center justify-center">
                <img src={badgeIconSrc} alt={badgeIconAlt} className="w-4 h-4 object-contain" />
              </div>
            )}
            <span className="text-callout text-gray-700">{blok.badge_label}</span>
          </div>
        )}
      </div>
    </>
  );

  // Render as <a> when link is present, otherwise as <article>
  if (hasLink) {
    return (
      <a {...editableProps} href={linkHref ?? undefined} className={linkClasses}>
        {cardContent}
      </a>
    );
  }

  return (
    <article {...editableProps} className={cardClasses}>
      {cardContent}
    </article>
  );
};
